import { useEffect } from 'react'
import './Landing.css'

export default function Landing() {
  useEffect(() => { document.title = 'CommitHQ — College Baseball Recruiting' }, [])

  return (
    <div className="lp-page">

      {/* ── Nav ── */}
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <a href="/" className="lp-nav-logo">
            <LogoMark />
            <span className="lp-wordmark">Commit<span className="lp-wordmark-hq">HQ</span></span>
          </a>
          <nav className="lp-nav-links">
            <a href="/login" className="lp-nav-signin">Sign In</a>
            <a href="/login" className="lp-btn-primary lp-btn-sm">Get Started Free</a>
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-badge">Built for Baseball Families</div>
          <h1 className="lp-hero-headline">
            Your Athlete's Recruiting<br className="lp-br" /> Journey, Organized.
          </h1>
          <p className="lp-hero-sub">
            CommitHQ gives baseball families a smarter way to manage the college
            recruiting process — without the agency fees.
          </p>
          <div className="lp-hero-ctas">
            <a href="/login" className="lp-btn-primary">Get Started Free</a>
            <a href="/athlete/commithq-sample-profile" className="lp-btn-outline">View Sample Profile</a>
          </div>
        </div>
        <div className="lp-hero-glow" aria-hidden="true" />
      </section>

      {/* ── Features ── */}
      <section className="lp-features">
        <div className="lp-section-inner">
          <h2 className="lp-section-headline">Everything you need in one place</h2>
          <div className="lp-features-grid">
            <FeatureCard
              icon="🎯"
              title="Pipeline Management"
              body="Track every school, coach contact, and recruiting status in one Kanban board built for baseball families."
            />
            <FeatureCard
              icon="👤"
              title="Athlete Profile Page"
              body="Build a shareable recruiting profile with stats, highlights, and contact info that coaches can find."
            />
            <FeatureCard
              icon="🏫"
              title="806 Programs"
              body="Browse every NCAA and JUCO baseball program with coach contacts, updated by families like yours."
            />
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="lp-how">
        <div className="lp-section-inner">
          <h2 className="lp-section-headline">Simple to start. Powerful as you grow.</h2>
          <div className="lp-steps">
            <Step
              number="1"
              title="Create your profile"
              body="Set up your athlete's recruiting page in minutes"
            />
            <StepArrow />
            <Step
              number="2"
              title="Build your pipeline"
              body="Add target schools and track your outreach"
            />
            <StepArrow />
            <Step
              number="3"
              title="Get noticed"
              body="Share your profile link with coaches directly"
            />
          </div>
        </div>
      </section>

      {/* ── Social Proof ── */}
      <section className="lp-proof">
        <div className="lp-section-inner">
          <h2 className="lp-section-headline">Built for baseball families</h2>
          <div className="lp-proof-stats">
            <StatBlock value="806" label="Programs" />
            <StatBlock value="Free" label="To Start" />
            <StatBlock value="No" label="Agency Needed" />
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-logo">
            <LogoMark size={24} />
            <span className="lp-footer-wordmark">Commit<span className="lp-wordmark-hq">HQ</span></span>
          </div>
          <p className="lp-footer-tagline">Built for baseball families</p>
          <nav className="lp-footer-links">
            <a href="/login">Sign In</a>
            <a href="/login">Get Started</a>
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
          </nav>
          <p className="lp-footer-copy">© {new Date().getFullYear()} CommitHQ</p>
        </div>
      </footer>

    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────

function FeatureCard({ icon, title, body }) {
  return (
    <div className="lp-feature-card">
      <span className="lp-feature-icon">{icon}</span>
      <h3 className="lp-feature-title">{title}</h3>
      <p className="lp-feature-body">{body}</p>
    </div>
  )
}

function Step({ number, title, body }) {
  return (
    <div className="lp-step">
      <div className="lp-step-number">{number}</div>
      <h3 className="lp-step-title">{title}</h3>
      <p className="lp-step-body">{body}</p>
    </div>
  )
}

function StepArrow() {
  return (
    <div className="lp-step-arrow" aria-hidden="true">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/>
        <polyline points="13 6 19 12 13 18"/>
      </svg>
    </div>
  )
}

function StatBlock({ value, label }) {
  return (
    <div className="lp-stat-block">
      <span className="lp-stat-value">{value}</span>
      <span className="lp-stat-label">{label}</span>
    </div>
  )
}

function LogoMark({ size = 32 }) {
  const h = Math.round(size * 1.125)
  return (
    <svg width={size} height={h} viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="64" height="72" rx="10" fill="#0D2340"/>
      <rect width="64" height="10" fill="#1D9E75"/>
      <rect width="64" height="5" y="5" fill="#1D9E75"/>
      <text x="32" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontSize="34" fontWeight="bold" fill="white">C</text>
      <text x="32" y="64" textAnchor="middle" fontFamily="Georgia, serif" fontSize="11" fontWeight="bold" fill="#1D9E75">HQ</text>
    </svg>
  )
}
